/**
 * Single source of truth for Supabase configuration
 * 
 * This ensures environment parity across:
 * - localhost (development)
 * - Vercel Production
 * - Vercel Preview/Development
 * - TestFlight Mobile
 * 
 * All Supabase clients (browser, server) should import from this file.
 */

/**
 * Get Supabase URL from environment
 * Must be identical across all environments
 */
export function getSupabaseUrl(): string {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  
  if (!url) {
    if (typeof window === "undefined") {
      // Server-side: throw error
      throw new Error(
        "NEXT_PUBLIC_SUPABASE_URL is not set. " +
        "Please add it to your environment variables. " +
        "Get it from: https://supabase.com/dashboard/project/YOUR_PROJECT/settings/api"
      );
    } else {
      // Client-side: log error but don't throw (allows graceful degradation)
      console.error(
        "NEXT_PUBLIC_SUPABASE_URL is not set. " +
        "Please add it to your .env.local file."
      );
      // Return placeholder to prevent crashes
      return "https://placeholder.supabase.co";
    }
  }
  
  return url;
}

/**
 * Get Supabase anon key from environment
 * Must be identical across all environments
 */
export function getSupabaseAnonKey(): string {
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  
  if (!key) {
    if (typeof window === "undefined") {
      // Server-side: throw error
      throw new Error(
        "NEXT_PUBLIC_SUPABASE_ANON_KEY is not set. " +
        "Please add it to your environment variables. " +
        "Get it from: https://supabase.com/dashboard/project/YOUR_PROJECT/settings/api"
      );
    } else {
      // Client-side: log error but don't throw
      console.error(
        "NEXT_PUBLIC_SUPABASE_ANON_KEY is not set. " +
        "Please add it to your .env.local file."
      );
      // Return placeholder to prevent crashes
      return "placeholder-key";
    }
  }
  
  return key;
}

/**
 * Get Supabase service role key from environment
 * Server-side only - NEVER expose to client
 */
export function getSupabaseServiceRoleKey(): string | undefined {
  // Service role key is optional (only needed for admin operations)
  // But if it's used, it must be set
  return process.env.SUPABASE_SERVICE_ROLE_KEY;
}

/**
 * Validate that Supabase config is properly set
 * Throws descriptive error if configuration is invalid
 */
export function validateSupabaseConfig(): void {
  const url = getSupabaseUrl();
  const anonKey = getSupabaseAnonKey();
  
  // Validate URL format
  if (!url.startsWith("https://") || !url.includes(".supabase.co")) {
    throw new Error(
      `Invalid NEXT_PUBLIC_SUPABASE_URL format: ${url}. ` +
      "Expected format: https://[project-ref].supabase.co"
    );
  }
  
  // Validate anon key format (JWT tokens start with eyJ)
  if (!anonKey.startsWith("eyJ")) {
    throw new Error(
      `Invalid NEXT_PUBLIC_SUPABASE_ANON_KEY format. ` +
      "Expected a JWT token starting with 'eyJ'"
    );
  }
  
  // Log config source for debugging (only in development)
  if (process.env.NODE_ENV === "development") {
    console.log("[Supabase Config] Validated configuration:", {
      url: url.substring(0, 30) + "...",
      hasAnonKey: !!anonKey,
      hasServiceRoleKey: !!getSupabaseServiceRoleKey(),
    });
  }
}

/**
 * Get complete Supabase configuration
 * Use this for creating Supabase clients
 */
export function getSupabaseConfig() {
  return {
    url: getSupabaseUrl(),
    anonKey: getSupabaseAnonKey(),
    serviceRoleKey: getSupabaseServiceRoleKey(),
  };
}
